import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Search, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from '@/components/ui/toaster';
import { useHasRole } from '@/features/auth/AuthProvider';
import type { Brand } from '@/types/schemas';
import { deleteBrand, fetchBrands, fetchCategories } from '../setupQueries';
import { fetchAllInventory } from '../inventoryQueries';
import { BrandFormDialog } from '../BrandFormDialog';
import { cn } from '@/lib/utils';

type SortField = 'name' | 'category' | 'products';

const CELL_BORDER = 'border border-border';

export function BrandsTab() {
  const qc = useQueryClient();
  const canWrite = useHasRole('owner');
  const canDelete = useHasRole('owner');
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [open, setOpen] = useState(false);

  const { data: brands = [], isLoading } = useQuery({
    queryKey: ['inventory-brands'],
    queryFn: () => fetchBrands(),
  });

  const { data: categories = [] } = useQuery({
    queryKey: ['inventory-categories'],
    queryFn: fetchCategories,
  });

  const { data: products = [] } = useQuery({
    queryKey: ['inventory'],
    queryFn: fetchAllInventory,
  });

  const categoryNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of categories) map.set(c.id, c.name);
    return map;
  }, [categories]);

  const productCountByBrand = useMemo(() => {
    const counts = new Map<string, number>();
    for (const product of products) {
      if (product.brandId) {
        counts.set(product.brandId, (counts.get(product.brandId) ?? 0) + 1);
      }
    }
    return counts;
  }, [products]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = brands;
    if (categoryFilter) {
      list = list.filter((b) => b.categoryId === categoryFilter);
    }
    if (q) {
      list = list.filter((b) => {
        const categoryName = categoryNameById.get(b.categoryId) ?? '';
        return (
          b.name.toLowerCase().includes(q) ||
          categoryName.toLowerCase().includes(q)
        );
      });
    }
    list = [...list].sort((a, b) => {
      let cmp = 0;
      if (sortField === 'name') cmp = a.name.localeCompare(b.name);
      else if (sortField === 'category') {
        const aCat = categoryNameById.get(a.categoryId) ?? '';
        const bCat = categoryNameById.get(b.categoryId) ?? '';
        cmp = aCat.localeCompare(bCat);
      } else {
        cmp = (productCountByBrand.get(a.id) ?? 0) - (productCountByBrand.get(b.id) ?? 0);
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return list;
  }, [brands, search, categoryFilter, sortField, sortDir, categoryNameById, productCountByBrand]);

  const remove = useMutation({
    mutationFn: deleteBrand,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inventory-brands'] });
      toast({ title: 'Brand deleted', variant: 'success' });
    },
    onError: (e) => toast({ title: 'Failed', description: String(e), variant: 'destructive' }),
  });

  function toggleSort(field: SortField) {
    if (sortField === field) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortField(field);
      setSortDir('asc');
    }
  }

  if (isLoading) return <p className="text-sm text-muted-foreground p-8 text-center">Loading…</p>;

  return (
    <>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            className="pl-8"
            placeholder="Search by brand or category…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          className="h-9 rounded-md border px-2 text-sm"
          value={categoryFilter ?? ''}
          onChange={(e) => setCategoryFilter(e.target.value || null)}
        >
          <option value="">All categories</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        {canWrite && (
          <Button variant="gold" onClick={() => setOpen(true)}>Add brand</Button>
        )}
      </div>

      <div className="mb-2 flex flex-wrap gap-2 text-xs">
        {(['name', 'category', 'products'] as SortField[]).map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => toggleSort(f)}
            className={cn(
              'rounded border px-2 py-1 capitalize',
              sortField === f ? 'bg-gold-100 border-gold-400' : 'text-muted-foreground',
            )}
          >
            {f} {sortField === f ? (sortDir === 'asc' ? '↑' : '↓') : ''}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <p className="p-8 text-center text-sm text-muted-foreground">No brands found.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full min-w-[560px] border-collapse text-sm">
            <thead>
              <tr className="bg-muted/40 text-[11px] font-semibold uppercase text-muted-foreground">
                <th className={cn(CELL_BORDER, 'px-3 py-2 text-left')}>Brand</th>
                <th className={cn(CELL_BORDER, 'px-3 py-2 text-left')}>Category</th>
                <th className={cn(CELL_BORDER, 'px-3 py-2 text-center')}>Products</th>
                <th className={cn(CELL_BORDER, 'px-3 py-2 text-right')}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((brand: Brand) => (
                <tr key={brand.id} className="hover:bg-muted/20">
                  <td className={cn(CELL_BORDER, 'px-3 py-2.5 font-medium')}>{brand.name}</td>
                  <td className={cn(CELL_BORDER, 'px-3 py-2.5 text-muted-foreground')}>
                    {categoryNameById.get(brand.categoryId) ?? '—'}
                  </td>
                  <td className={cn(CELL_BORDER, 'px-3 py-2.5 text-center tabular-nums')}>
                    {productCountByBrand.get(brand.id) ?? 0}
                  </td>
                  <td className={cn(CELL_BORDER, 'px-3 py-2.5')}>
                    <div className="flex justify-end">
                      {canDelete && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-destructive"
                          onClick={() => {
                            if (confirm(`Delete brand "${brand.name}"?`)) remove.mutate(brand.id);
                          }}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="mt-2 text-xs text-muted-foreground">
        {filtered.length} of {brands.length} brands · Mapped brands appear when adding a product under the same category
      </p>

      <BrandFormDialog
        open={open}
        onOpenChange={setOpen}
        defaultCategoryId={categoryFilter ?? undefined}
      />
    </>
  );
}
